import { cn } from "../../lib/utils";
import React, { useLayoutEffect, useRef, useState } from "react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../ui/resizable";
import { ScrollArea } from "../ui/scroll-area";

interface ResizableLayoutProps {
  children: React.ReactNode;
  className?: string;
}

interface MainProps {
  children: React.ReactNode;
  className?: string;
  scrollAreaClassName?: string;
}

interface PanelHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface PanelTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface PanelProps {
  children?: React.ReactNode;
  className?: string;
}

interface PanelContentProps {
  children: React.ReactNode;
  className?: string;
  scrollAreaClassName?: string;
}

interface LayoutHeaderProps {
  children: React.ReactNode;
  className?: string;
}

type ResizableLayoutComponent = React.FC<ResizableLayoutProps> & {
  Main: React.FC<MainProps>;
  Panel: React.FC<PanelProps>;
  PanelHeader: React.FC<PanelHeaderProps>;
  PanelTitle: React.FC<PanelTitleProps>;
  PanelContent: React.FC<PanelContentProps>;
  Header: React.FC<LayoutHeaderProps>;
};

const ResizableLayout: ResizableLayoutComponent = ({ children, className }) => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  // Convert children to array to allow filtering
  const childrenArray = React.Children.toArray(children);

  // Extract header component from children
  const headerComponent = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === Header,
  );

  // Get content components (everything except header)
  const contentComponents = childrenArray.filter(
    (child) => !React.isValidElement(child) || child.type !== Header,
  );

  // Measure header height after it renders
  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [headerComponent]);

  return (
    <div
      className={cn(
        "h-[calc(100dvh-4rem)] w-full rounded-tl-lg border bg-background",
        className,
      )}
      style={
        headerHeight
          ? ({ "--header-height": `${headerHeight}px` } as React.CSSProperties)
          : {}
      }
    >
      {headerComponent && <div ref={headerRef}>{headerComponent}</div>}
      <div
        className={
          headerHeight ? "h-[calc(100%-var(--header-height))]" : "h-full"
        }
      >
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {contentComponents}
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

const Header: React.FC<LayoutHeaderProps> = ({ children, className }) => {
  return <div className={cn("w-full p-4 border-b", className)}>{children}</div>;
};

const Main: React.FC<MainProps> = ({
  children,
  className,
  scrollAreaClassName,
}) => {
  return (
    <ResizablePanel
      defaultSize={66}
      minSize={33}
      maxSize={75}
      className="h-full overflow-hidden"
    >
      <ScrollArea className={cn("h-full", scrollAreaClassName)}>
        <div className={cn("flex flex-col p-6 min-w-0", className)}>{children}</div>
      </ScrollArea>
    </ResizablePanel>
  );
};

const PanelHeader: React.FC<PanelHeaderProps> = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between border-b p-4",
        className,
      )}
    >
      {children}
    </div>
  );
};

const PanelTitle: React.FC<PanelTitleProps> = ({ children, className }) => {
  return <h3 className={cn("font-semibold", className)}>{children}</h3>;
};

const Panel: React.FC<PanelProps> = ({ children, className }) => {
  if (!children) return null;

  return (
    <>
      <ResizableHandle withHandle />
      <ResizablePanel 
        defaultSize={34} 
        minSize={25}
        maxSize={67}
        className="h-full overflow-hidden"
      >
        <div className={cn("flex h-full flex-col min-w-0", className)}>{children}</div>
      </ResizablePanel>
    </>
  );
};

const PanelContent: React.FC<PanelContentProps> = ({
  children,
  className,
  scrollAreaClassName,
}) => {
  return (
    <ScrollArea className={cn("flex-1 min-h-0", scrollAreaClassName)}>
      <div className={cn("p-4 min-w-0", className)}>{children}</div>
    </ScrollArea>
  );
};

ResizableLayout.Main = Main;
ResizableLayout.Panel = Panel;
ResizableLayout.PanelHeader = PanelHeader;
ResizableLayout.PanelTitle = PanelTitle;
ResizableLayout.PanelContent = PanelContent;
ResizableLayout.Header = Header;

export { ResizableLayout };

export type {
  LayoutHeaderProps,
  MainProps,
  PanelContentProps,
  PanelHeaderProps,
  PanelProps,
  PanelTitleProps,
  ResizableLayoutProps,
};
